/**
 * Pure notification-settings logic for UserSettingsModal.
 *
 * Extracted from UserSettingsModal.tsx so migration paths and settings
 * state transitions can be unit tested without React (see Testing Pattern
 * in CLAUDE.md).
 *
 * Settings schema (v3.1 - per-page model): { enabled, pages: { rentalBoard?,
 * yard?, truckingCalendar?, ... } } where each page has { inApp, push,
 * locations } plus optional trucking-calendar advanced fields.
 * See PageNotificationSettings in @/types/notification.types.
 *
 * Migration from v2 (categories): yard category → yard page, sales category
 * → rentalBoard page. Migration from early v3: rentals page → rentalBoard +
 * yard pages (Jan 2026 split).
 */

import {
  NOTIFICATION_PAGES,
  NOTIFICATION_PAGE_REGISTRY,
  NOTIFICATION_PAGE_IDS,
  buildPageRecord,
  type NotificationPageId,
} from '../../../config/notificationPageRegistry';
import type {
  NotificationSettings,
  PageNotificationSettings,
} from '@/types/notification.types';

export const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: false,
  pages: {},
};

const DEFAULT_PAGE_SETTINGS: PageNotificationSettings = {
  inApp: true,
  push: true,
  locations: [],
};

/** Status transitions configurable for trucking calendar notifications */
export type StatusTransition = 'toScheduled' | 'toPickedUp' | 'toDelivered';

/**
 * Legacy notification settings (v2 - category-based)
 */
export interface LegacyNotificationSettings {
  enabled?: boolean;
  categories?: ('yard' | 'sales')[];
  categoryLocations?: {
    yard?: string[];
    sales?: string[];
  };
  locations?: string[]; // v1 global locations
}

/**
 * Migrate legacy category-based settings to new page-based format
 *
 * Migration paths:
 * 1. v2 categories (yard, sales) → v3 pages (rentalBoard, yard)
 * 2. Early v3 'rentals' page → split into 'rentalBoard' and 'yard' pages
 */
export function migrateSettings(
  legacy: LegacyNotificationSettings,
  accessibleLocationsByPage: Record<NotificationPageId, string[]>
): NotificationSettings {
  const settings: NotificationSettings = {
    enabled: legacy.enabled ?? false,
    pages: {},
  };

  // Check if already in v3 format (has 'pages' key)
  if ('pages' in legacy && legacy.pages) {
    const v3Settings = legacy as unknown as NotificationSettings;

    // Check if using old 'rentals' page that needs to be split
    if (v3Settings.pages.rentals && !v3Settings.pages.rentalBoard && !v3Settings.pages.yard) {
      const rentalsSettings = v3Settings.pages.rentals;

      // Migrate rentals → rentalBoard (same settings)
      settings.pages.rentalBoard = {
        inApp: rentalsSettings.inApp,
        push: rentalsSettings.push,
        locations: rentalsSettings.locations.filter((l) =>
          accessibleLocationsByPage.rentalBoard.includes(l)
        ),
      };

      // Migrate rentals → yard (same settings, filtered by yard access)
      settings.pages.yard = {
        inApp: rentalsSettings.inApp,
        push: rentalsSettings.push,
        locations: rentalsSettings.locations.filter((l) =>
          accessibleLocationsByPage.yard.includes(l)
        ),
      };

      // Preserve other page settings
      if (v3Settings.pages.truckingCalendar) settings.pages.truckingCalendar = v3Settings.pages.truckingCalendar;

      return settings;
    }

    // Already using new page IDs, return as-is
    return v3Settings;
  }

  // Migrate from v2 category-based settings
  const categories = legacy.categories || [];
  const categoryLocations = legacy.categoryLocations || {};
  const globalLocations = legacy.locations || [];

  // Migrate sales category → rentalBoard page
  const hasSales = categories.includes('sales');
  if (hasSales) {
    const salesLocations = categoryLocations.sales || [];
    const locations =
      salesLocations.length > 0
        ? salesLocations
        : globalLocations.filter((l) => accessibleLocationsByPage.rentalBoard.includes(l));

    settings.pages.rentalBoard = {
      inApp: true,
      push: true,
      locations: locations.filter((l) => accessibleLocationsByPage.rentalBoard.includes(l)),
    };
  }

  // Migrate yard category → yard page
  const hasYard = categories.includes('yard');
  if (hasYard) {
    const yardLocations = categoryLocations.yard || [];
    const locations =
      yardLocations.length > 0
        ? yardLocations
        : globalLocations.filter((l) => accessibleLocationsByPage.yard.includes(l));

    settings.pages.yard = {
      inApp: true,
      push: true,
      locations: locations.filter((l) => accessibleLocationsByPage.yard.includes(l)),
    };
  }

  return settings;
}

/**
 * Get location IDs from a page permission object.
 * The special 'all' key expands to every known location ID.
 */
export function getLocationIdsFromPageAccess(pageAccess: unknown, allLocationIds: string[]): string[] {
  const access = pageAccess as { locations?: Record<string, unknown> } | null | undefined;
  if (!access?.locations) return [];
  const ids: string[] = [];
  Object.keys(access.locations).forEach((id) => {
    if (id === 'all') {
      allLocationIds.forEach((locId) => ids.push(locId));
    } else {
      ids.push(id);
    }
  });
  return [...new Set(ids)].sort();
}

/**
 * Get accessible locations for each notification page based on the user's
 * permissions. Admins get every location for every page.
 */
export function buildAccessibleLocationsByPage(
  user: { role: string; pagePermissions?: unknown } | null,
  allLocationIds: string[]
): Record<NotificationPageId, string[]> {
  if (!user) {
    return buildPageRecord<string[]>(() => []);
  }

  if (user.role === 'admin') {
    return buildPageRecord<string[]>(() => [...allLocationIds]);
  }

  const access = (user.pagePermissions || {}) as Record<string, unknown>;
  const result = buildPageRecord<string[]>(() => []);
  for (const id of NOTIFICATION_PAGE_IDS) {
    const key = NOTIFICATION_PAGE_REGISTRY[id].pagePermissionKey;
    result[id] = getLocationIdsFromPageAccess(access[key], allLocationIds);
  }
  return result;
}

/**
 * Determine which pages the user has access to (non-empty location list)
 */
export function getAccessiblePages(
  accessibleLocationsByPage: Record<NotificationPageId, string[]>
): NotificationPageId[] {
  return NOTIFICATION_PAGES
    .filter((page) => accessibleLocationsByPage[page.id].length > 0)
    .map((page) => page.id);
}

/**
 * Filter loaded (already migrated) settings down to the pages and locations
 * the user can actually access, applying per-page defaults.
 */
export function filterSettingsToAccessible(
  migratedSettings: NotificationSettings,
  accessiblePages: NotificationPageId[],
  accessibleLocationsByPage: Record<NotificationPageId, string[]>
): NotificationSettings {
  const filteredSettings: NotificationSettings = {
    enabled: migratedSettings.enabled,
    pages: {},
  };

  for (const pageId of accessiblePages) {
    const pageSettings = migratedSettings.pages[pageId];
    if (pageSettings) {
      filteredSettings.pages[pageId] = {
        inApp: pageSettings.inApp ?? true,
        push: pageSettings.push ?? true,
        locations: (pageSettings.locations || []).filter((l) =>
          accessibleLocationsByPage[pageId].includes(l)
        ),
      };
    }
  }

  return filteredSettings;
}

/**
 * Toggle the master notification switch.
 * Toggling on with no page settings initializes every accessible page with
 * all locations selected (smart default).
 */
export function applyMasterToggle(
  prev: NotificationSettings,
  accessiblePages: NotificationPageId[],
  accessibleLocationsByPage: Record<NotificationPageId, string[]>
): NotificationSettings {
  // Toggling off - simple
  if (prev.enabled) {
    return { ...prev, enabled: false };
  }

  // Toggling on - initialize with defaults if empty
  const hasAnyPageSettings = Object.keys(prev.pages).length > 0;

  if (!hasAnyPageSettings) {
    // Initialize all accessible pages with all locations selected
    const initialPages: NotificationSettings['pages'] = {};

    for (const pageId of accessiblePages) {
      initialPages[pageId] = {
        inApp: true,
        push: true,
        locations: [...accessibleLocationsByPage[pageId]],
      };
    }

    return {
      enabled: true,
      pages: initialPages,
    };
  }

  return { ...prev, enabled: true };
}

/**
 * Toggle notifications for a page (simplified - enables both inApp and push)
 * When enabled, auto-selects all accessible locations (smart default)
 */
export function applyTogglePage(
  prev: NotificationSettings,
  pageId: NotificationPageId,
  accessibleLocations: string[]
): NotificationSettings {
  const pageSettings = prev.pages[pageId];
  const isCurrentlyEnabled = pageSettings ? (pageSettings.inApp || pageSettings.push) : false;

  if (isCurrentlyEnabled) {
    // Disable - set both to false
    return {
      ...prev,
      pages: {
        ...prev.pages,
        [pageId]: {
          ...pageSettings,
          inApp: false,
          push: false,
        },
      },
    };
  } else {
    // Enable - set both to true with all locations (smart default)
    return {
      ...prev,
      pages: {
        ...prev.pages,
        [pageId]: {
          inApp: true,
          push: true,
          locations: [...accessibleLocations], // Smart default: all locations
          // Preserve any existing advanced settings
          ...(pageSettings?.reminderEnabled !== undefined && { reminderEnabled: pageSettings.reminderEnabled }),
          ...(pageSettings?.reminderMinutes !== undefined && { reminderMinutes: pageSettings.reminderMinutes }),
          ...(pageSettings?.newEventEnabled !== undefined && { newEventEnabled: pageSettings.newEventEnabled }),
          ...(pageSettings?.statusChangeEnabled !== undefined && { statusChangeEnabled: pageSettings.statusChangeEnabled }),
          ...(pageSettings?.statusTransitions !== undefined && { statusTransitions: pageSettings.statusTransitions }),
        },
      },
    };
  }
}

/**
 * Toggle a specific location for a page
 */
export function applyToggleLocation(
  prev: NotificationSettings,
  pageId: NotificationPageId,
  locationId: string
): NotificationSettings {
  const pageSettings = prev.pages[pageId] || { ...DEFAULT_PAGE_SETTINGS };
  const currentLocations = pageSettings.locations || [];
  const newLocations = currentLocations.includes(locationId)
    ? currentLocations.filter((id) => id !== locationId)
    : [...currentLocations, locationId];

  return {
    ...prev,
    pages: {
      ...prev.pages,
      [pageId]: {
        ...pageSettings,
        locations: newLocations,
      },
    },
  };
}

/**
 * Toggle all locations for a page (deselect all when every location is
 * already selected, otherwise select all)
 */
export function applyToggleAllLocations(
  prev: NotificationSettings,
  pageId: NotificationPageId,
  accessibleLocations: string[]
): NotificationSettings {
  const pageSettings = prev.pages[pageId] || { ...DEFAULT_PAGE_SETTINGS };
  const currentLocations = pageSettings.locations || [];
  const allSelected = currentLocations.length === accessibleLocations.length;

  return {
    ...prev,
    pages: {
      ...prev.pages,
      [pageId]: {
        ...pageSettings,
        locations: allSelected ? [] : [...accessibleLocations],
      },
    },
  };
}

/**
 * Set reminder timing for a page (truckingCalendar)
 */
export function applySetReminderMinutes(
  prev: NotificationSettings,
  pageId: NotificationPageId,
  minutes: number
): NotificationSettings {
  const pageSettings = prev.pages[pageId] || { ...DEFAULT_PAGE_SETTINGS };

  return {
    ...prev,
    pages: {
      ...prev.pages,
      [pageId]: {
        ...pageSettings,
        reminderMinutes: minutes,
      },
    },
  };
}

/**
 * Toggle reminder notifications for trucking calendar
 */
export function applyToggleReminderEnabled(
  prev: NotificationSettings,
  pageId: NotificationPageId
): NotificationSettings {
  const pageSettings = prev.pages[pageId] || { ...DEFAULT_PAGE_SETTINGS };

  return {
    ...prev,
    pages: {
      ...prev.pages,
      [pageId]: {
        ...pageSettings,
        reminderEnabled: pageSettings.reminderEnabled === false ? true : false,
      },
    },
  };
}

/**
 * Toggle new event notifications for trucking calendar
 */
export function applyToggleNewEventEnabled(
  prev: NotificationSettings,
  pageId: NotificationPageId
): NotificationSettings {
  const pageSettings = prev.pages[pageId] || { ...DEFAULT_PAGE_SETTINGS };

  return {
    ...prev,
    pages: {
      ...prev.pages,
      [pageId]: {
        ...pageSettings,
        newEventEnabled: pageSettings.newEventEnabled === false ? true : false,
      },
    },
  };
}

/**
 * Toggle status change notifications for trucking calendar
 */
export function applyToggleStatusChangeEnabled(
  prev: NotificationSettings,
  pageId: NotificationPageId
): NotificationSettings {
  const pageSettings = prev.pages[pageId] || { ...DEFAULT_PAGE_SETTINGS };

  return {
    ...prev,
    pages: {
      ...prev.pages,
      [pageId]: {
        ...pageSettings,
        statusChangeEnabled: pageSettings.statusChangeEnabled === false ? true : false,
      },
    },
  };
}

/**
 * Toggle a specific status transition setting for trucking calendar
 */
export function applyToggleStatusTransition(
  prev: NotificationSettings,
  pageId: NotificationPageId,
  transition: StatusTransition
): NotificationSettings {
  const pageSettings = prev.pages[pageId] || { ...DEFAULT_PAGE_SETTINGS };
  const currentTransitions = pageSettings.statusTransitions || {
    toScheduled: true,
    toPickedUp: true,
    toDelivered: true,
  };

  return {
    ...prev,
    pages: {
      ...prev.pages,
      [pageId]: {
        ...pageSettings,
        statusTransitions: {
          ...currentTransitions,
          [transition]: !currentTransitions[transition],
        },
      },
    },
  };
}

/**
 * Check if a page has notifications enabled
 */
export function isPageEnabled(settings: NotificationSettings, pageId: NotificationPageId): boolean {
  const pageSettings = settings.pages[pageId];
  return pageSettings ? (pageSettings.inApp || pageSettings.push) : false;
}

/**
 * Get location summary text for a page
 */
export function getLocationSummary(
  settings: NotificationSettings,
  pageId: NotificationPageId,
  accessibleLocations: string[]
): string {
  const pageSettings = settings.pages[pageId];
  const selectedCount = pageSettings?.locations?.length || 0;

  if (selectedCount === 0) return 'No locations';
  if (selectedCount === accessibleLocations.length) return 'All locations';
  return `${selectedCount} of ${accessibleLocations.length} locations`;
}

/**
 * Deep, key-order-insensitive equality for settings objects — drives the
 * modal's "Save Changes" enabled state (disabled until something changed).
 * Location arrays are compared as sets: toggling a location off and back on
 * re-appends it, which is not a change. `undefined` fields count as absent.
 */
export function areSettingsEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const isPrimitive = (v: unknown) => v === null || typeof v !== 'object';
    if (a.every(isPrimitive) && b.every(isPrimitive)) {
      const setB = new Set(b);
      return a.every((v) => setB.has(v)) && new Set(a).size === setB.size;
    }
    return a.every((v, i) => areSettingsEqual(v, b[i]));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const ra = a as Record<string, unknown>;
    const rb = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(ra), ...Object.keys(rb)]);
    for (const key of keys) {
      if (!areSettingsEqual(ra[key], rb[key])) return false;
    }
    return true;
  }
  return false;
}
